import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AppState } from '@app/core/states/app.state';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { UiModule } from '@app/ui';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import {
  PublishingPlatform,
  PublishingStepStatus,
  PublishingStepType,
} from '../../models/publishing.model';
import { UpdateCompletedStepRequest } from '../../states/publishing.actions';
import { TutorialModalComponent } from '../tutorial-modal/tutorial-modal.component';
import { PublishingStepPanelComponent } from './publishing-step-panel/publishing-step-panel.component';
import { StepStatusIconComponent } from './step-status-icon/step-status-icon.component';

const stepContentMapping = {
  android: {
    generation: {
      label: marker('publishing-step.generation-label'),
      description: marker('publishing-step.generation-description'),
      cta: marker('publishing-step.generation-cta'),
    },
    download: {
      label: marker('publishing-step.download-label'),
      description: marker('publishing-step.download-description'),
      cta: marker('publishing-step.download-cta'),
    },
    tutorial: {
      label: marker('publishing-step.android-tutorial-label'),
      description: marker('publishing-step.android-tutorial-description'),
      cta: marker('publishing-step.tutorial-cta'),
    },
    published: {
      label: marker('publishing-step.published-label'),
      description: null,
      cta: null,
    },
    'dev-account': {
      label: marker('publishing-step.android-dev-account-label'),
      description: marker('publishing-step.android-dev-account-description'),
      cta: marker('publishing-step.android-dev-account-cta'),
    },
  },
  ios: {
    generation: {
      label: marker('publishing-step.generation-label'),
      description: marker('publishing-step.generation-description'),
      cta: marker('publishing-step.generation-cta'),
    },
    download: {
      label: marker('publishing-step.download-label'),
      description: marker('publishing-step.download-description'),
      cta: marker('publishing-step.download-cta'),
    },
    tutorial: {
      label: marker('publishing-step.ios-tutorial-label'),
      description: marker('publishing-step.ios-tutorial-description'),
      cta: marker('publishing-step.tutorial-cta'),
    },
    published: {
      label: marker('publishing-step.published-label'),
      description: null,
      cta: null,
    },
    'dev-account': {
      label: marker('publishing-step.ios-dev-account-label'),
      description: marker('publishing-step.ios-dev-account-description'),
      cta: marker('publishing-step.ios-dev-account-cta'),
    },
    carplay: {
      label: marker('publishing-step.ios-carplay-label'),
      description: marker('publishing-step.ios-carplay-description'),
      cta: marker('publishing-step.ios-carplay-cta'),
    },
  },
};

@Component({
  selector: 'rk-publishing-step',
  imports: [
    UiModule,
    FontAwesomeModule,
    TranslatePipe,
    StepStatusIconComponent,
    MatDialogModule,
    PublishingStepPanelComponent,
  ],
  templateUrl: './publishing-step.component.html',
  styleUrl: './publishing-step.component.scss',
})
export class PublishingStepComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() stepType!: PublishingStepType;
  @Input() status!: PublishingStepStatus;
  @Input() platform!: PublishingPlatform;
  @Input() ctaLink?: string;
  @Input() isLastStep = false;

  // Flow line properties
  showFlowLine = false;
  flowLineCompleted = false;
  isOpen = false;
  isExpanding = false; // Add this property
  private _cachedFlowLineHeight = 0;
  private readonly CLOSED_STEP_FLOW_LINE_HEIGHT = 33;

  @Output() readonly stepToggle = new EventEmitter<void>();

  @ViewChild('stepContainer', { static: true }) stepContainer!: ElementRef;

  currentAppId$: Observable<number> = inject(Store).select(AppState.currentAppId);

  private readonly subscription = new Subscription();
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  get stepContent() {
    return stepContentMapping[this.platform][this.stepType];
  }

  ngOnInit() {
    if (!this.stepType || !this.status || !this.platform) {
      console.warn('PublishingStepComponent: Missing required inputs');
    }

    if (this.status === 'current' || this.status === 'in_progress') {
      this.isOpen = true;
    }
  }

  ngAfterViewInit() {
    // Initialize flow line height after view is initialized
    setTimeout(() => this.updateFlowLineHeight(), 0);
  }

  onCtaClick() {
    if (!this.stepType || !this.status || !this.platform) {
      console.error('Cannot proceed: Missing required inputs');

      return;
    }

    const currentAppId = this.store.selectSnapshot(AppState.currentAppId);

    // if step is tutorial, open modal before validating step
    if (this.stepType === 'tutorial' || this.stepType === 'carplay') {
      if (!this.ctaLink) {
        console.warn('No tutorial URL provided');

        return;
      }

      const dialogRef = this.dialog.open(TutorialModalComponent, {
        data: { url: this.ctaLink },
        width: '90%',
        height: '90%',
        maxWidth: '90vw',
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          if (currentAppId) {
            this.store.dispatch(
              new UpdateCompletedStepRequest(currentAppId, this.platform, this.stepType),
            );
          }
        }
      });
    } else {
      // else open link and validate step
      if (this.ctaLink) {
        window.open(this.ctaLink, '_blank');
        if (currentAppId) {
          this.store.dispatch(
            new UpdateCompletedStepRequest(currentAppId, this.platform, this.stepType),
          );
        }
      }
    }

    // tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `ButtonPublishingStep${this.stepType}`,
        component: 'Publishing',
      }),
    );
    this.store.dispatch(new LogEvent('Button Clicked'));
  }

  onExistingDevAccountClick() {
    const currentAppId = this.store.selectSnapshot(AppState.currentAppId);
    this.store.dispatch(
      new UpdateCompletedStepRequest(currentAppId, this.platform, this.stepType),
    );
  }

  handleStepToggle(): void {
    this.stepToggle.emit();
    const wasOpen = this.isOpen;
    this.isOpen = !this.isOpen;

    // Set expanding state based on whether we're opening or closing
    this.isExpanding = !wasOpen; // true when opening, false when closing

    setTimeout(() => {
      this.updateFlowLineHeight();
      // Reset expanding state after transition
      setTimeout(() => (this.isExpanding = false), 300);
    }, 0);
  }

  getStepHeight(): number {
    return this.stepContainer?.nativeElement?.offsetHeight || 0;
  }

  getStatusTag(status: PublishingStepStatus): string | null {
    return status === 'in_progress' && marker('publishing-step.in-progress');
  }

  getIsOpen(status: PublishingStepStatus): boolean {
    return status === 'current' || status === 'in_progress';
  }

  getCtaIsDisabled(status: PublishingStepStatus): boolean {
    if (
      (this.stepType === 'tutorial' || this.stepType === 'carplay' || this.stepType === 'download') &&
      status !== 'pending'
    ) {
      return false;
    }

    return status !== 'current';
  }

  get flowLineHeight(): number {
    return this._cachedFlowLineHeight;
  }

  // Call this method when you need to update the flow line height
  updateFlowLineHeight(): void {
    if (this.isLastStep) {
      this._cachedFlowLineHeight = 0;

      return;
    }

    // If step is closed, use fixed height
    if (!this.isOpen) {
      this._cachedFlowLineHeight = this.CLOSED_STEP_FLOW_LINE_HEIGHT;

      return;
    }

    // If step is open, calculate dynamic height based on content
    setTimeout(() => {
      this._cachedFlowLineHeight = this.calculateDynamicHeight();
    }, 250);
  }

  private calculateDynamicHeight(): number {
    const stepEl = this.stepContainer?.nativeElement;
    if (!stepEl) {
      return this.CLOSED_STEP_FLOW_LINE_HEIGHT;
    }

    const contentHeight = stepEl.scrollHeight;
    const iconCenterOffset = 21;

    return contentHeight - iconCenterOffset;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
